import React from 'react';
import { motion } from 'framer-motion';
import { FiClock, FiDollarSign } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLanguage } from '../i18n/LanguageContext';

const ServiceCard = ({ title, description, price, duration, note, bookLabel }) => (
  <div className="bg-white p-8 rounded-2xl shadow-soft h-full flex flex-col">
    <h3 className="font-serif text-2xl text-brand-dark mb-3">{title}</h3>
    <p className="text-brand-muted text-sm mb-6 flex-grow leading-relaxed">{description}</p>
    <div className="pt-6 border-t border-brand-sand mt-auto flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4 text-sm text-brand-dark font-medium">
        {price && (
          <span className="flex items-center gap-1">
            <SafeIcon icon={FiDollarSign} className="w-4 h-4 text-brand-taupe" />
            {price}
          </span>
        )}
        {duration && (
          <span className="flex items-center gap-1">
            <SafeIcon icon={FiClock} className="w-4 h-4 text-brand-taupe" />
            {duration}
          </span>
        )}
        {note && <span className="text-brand-taupe italic">{note}</span>}
      </div>
      <a 
        href="#"
        className="w-full block text-center bg-brand-sand hover:bg-brand-taupe hover:text-white text-brand-dark py-3 rounded-full transition-colors text-sm font-medium"
      >
        {bookLabel}
      </a>
    </div>
  </div>
);

const Services = () => {
  const { t } = useLanguage();
  const cards = t('services.cards');
  const additional = t('services.additional');

  return (
    <section id="services" className="py-24 bg-brand-cream">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <h2 className="font-serif text-3xl md:text-4xl text-brand-dark mb-4">{t('services.title')}</h2>
          <p className="text-brand-muted text-lg font-light">{t('services.subtitle')}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <ServiceCard 
              title={cards?.[0]?.title}
              description={cards?.[0]?.description}
              price={cards?.[0]?.price}
              duration={cards?.[0]?.duration}
              bookLabel={t('services.bookNow')}
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <ServiceCard 
              title={cards?.[1]?.title}
              description={cards?.[1]?.description}
              note={cards?.[1]?.note}
              bookLabel={t('services.bookNow')}
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <ServiceCard 
              title={cards?.[2]?.title}
              description={cards?.[2]?.description}
              note={cards?.[2]?.note}
              bookLabel={t('services.bookNow')}
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <ServiceCard 
              title={cards?.[3]?.title}
              description={cards?.[3]?.description}
              note={cards?.[3]?.note}
              bookLabel={t('services.bookNow')}
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <ServiceCard 
              title={cards?.[4]?.title}
              description={cards?.[4]?.description}
              note={cards?.[4]?.note}
              bookLabel={t('services.bookNow')}
            />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}>
            <ServiceCard 
              title={cards?.[5]?.title}
              description={cards?.[5]?.description}
              price={cards?.[5]?.price}
              duration={cards?.[5]?.duration}
              bookLabel={t('services.bookNow')}
            />
          </motion.div>
        </div>

        {/* Additional Services */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          viewport={{ once: true }}
          className="max-w-3xl mx-auto bg-brand-sand rounded-2xl p-8 md:p-12"
        >
          <h3 className="font-serif text-2xl text-brand-dark mb-6 text-center">{t('services.additionalTitle')}</h3>
          <ul className="divide-y divide-brand-taupe/20">
            <li className="py-4 flex justify-between items-center">
              <div>
                <span className="block font-medium text-brand-dark">{additional?.removal}</span>
                <span className="text-sm text-brand-muted">{additional?.min30}</span>
              </div>
              <span className="font-medium text-brand-dark">$25</span>
            </li>
            <li className="py-4 flex justify-between items-center">
              <div>
                <span className="block font-medium text-brand-dark">{additional?.patchTest}</span>
                <span className="text-sm text-brand-muted">{additional?.min20}</span>
              </div>
              <span className="font-medium text-brand-dark">{additional?.free}</span>
            </li>
            <li className="py-4 flex justify-between items-center">
              <div>
                <span className="block font-medium text-brand-dark">{additional?.homeService}</span>
                <span className="text-sm text-brand-muted">{additional?.travelFee}</span>
              </div>
              <span className="font-medium text-brand-dark">{additional?.startingAt}</span>
            </li>
          </ul>
        </motion.div>

      </div>
    </section>
  );
};

export default Services;
